// Generated from products/analytics/sdk/staticSite.ts; run analytics generate:site-runtime.
import {
  createAnalyticsClient,
  createBrowserEnvironment,
  analyticsEventId,
  type AnalyticsClient,
} from "./client.gen";
import {
  BrowserAnalyticsConfigSchema,
  type BrowserAnalyticsConfig,
} from "./domainConfig.gen";

export type StaticSiteOptions = {
  configs: readonly BrowserAnalyticsConfig[];
  excludedPrefixes: readonly string[];
  /** Explicit public destinations -> fixed labels; never arbitrary href/text/input. */
  navigation: Readonly<Record<string, string>>;
  captureEngagement?: boolean;
};
type Browser = {
  location: Pick<Location, "protocol" | "hostname" | "pathname" | "search">;
  navigator: Pick<Navigator, "doNotTrack"> & { globalPrivacyControl?: boolean };
};
const privatePath = /^\/(?:_|(?:api|admin|account|login|logout|checkout|contact|404)(?:\/|\.|$))/;
export function staticCollectionAllowed(browser: Browser, options: StaticSiteOptions): boolean {
  const { location, navigator } = browser;
  const path = location.pathname;
  return (
    location.protocol === "https:" &&
    options.configs.some((config) => config.hostname === location.hostname) &&
    navigator.doNotTrack !== "1" &&
    navigator.globalPrivacyControl !== true &&
    /^\/(?!\/)[^?#%\\\s]*$/.test(path) &&
    !location.search &&
    !privatePath.test(path) &&
    !options.excludedPrefixes.some((prefix) => path === prefix || path.startsWith(prefix + "/")) &&
    !(/\.[^/]+\/?$/.test(path) && !/\.html\/?$/.test(path))
  );
}

/** Browser-only installation. DEV noindex is independent from collection permission. */
export function installStaticSiteAnalytics(options: StaticSiteOptions) {
  const configs = options.configs.map((config) => BrowserAnalyticsConfigSchema.parse(config));
  const allowed = () => staticCollectionAllowed(window, { ...options, configs });
  let client: AnalyticsClient | undefined;
  let lastPath: string | undefined;
  const ready = () => {
    if (!allowed()) {
      client?.close();
      client = undefined;
      lastPath = undefined;
      return;
    }
    if (!client) {
      const config = configs.find((entry) => entry.hostname === window.location.hostname);
      if (!config) return;
      client = createAnalyticsClient({
        config,
        extendedTelemetry: false,
        captureEngagement: options.captureEngagement ?? true,
        environment: { ...createBrowserEnvironment(), referrer: () => "" },
        collectionAllowed: allowed,
      });
    }
    return client;
  };
  const page = () => {
    try {
      const current = ready();
      if (!current || lastPath === window.location.pathname) return;
      lastPath = window.location.pathname;
      current.track({
        type: analyticsEventId.pageViewed,
        payload: { title: document.title.slice(0, 300) },
      });
    } catch {
      /* Collection never interrupts the website. */
    }
  };
  const click = (event: MouseEvent) => {
    try {
      if (event.defaultPrevented || event.button !== 0) return;
      const element = event.target instanceof Element ? event.target.closest("a[href]") : null;
      if (!(element instanceof HTMLAnchorElement)) return;
      const url = new URL(element.href);
      if (
        url.origin !== window.location.origin ||
        url.search ||
        url.hash ||
        url.username ||
        url.password
      )
        return;
      const resourceId = options.navigation[url.pathname];
      if (!resourceId || !/^[A-Za-z0-9][A-Za-z0-9_.-]{0,119}$/.test(resourceId)) return;
      const current = ready();
      current?.track({
        type: analyticsEventId.siteAction,
        payload: { name: "public_navigation_clicked", resourceId },
      });
      void current?.flush();
    } catch {
      /* Best effort; native navigation remains immediate. */
    }
  };
  document.addEventListener("click", click, true);
  return {
    page,
    navigationClicked(resourceId: string) {
      if (!/^[A-Za-z0-9][A-Za-z0-9_.-]{0,119}$/.test(resourceId)) return;
      try {
        ready()?.track({
          type: analyticsEventId.siteAction,
          payload: { name: "public_navigation_clicked", resourceId },
        });
      } catch {
        /* Never interrupt navigation. */
      }
    },
    gameEntered() {
      try {
        ready()?.track({ type: analyticsEventId.gameEntered, payload: {} });
      } catch {
        /* Collection never interrupts the game. */
      }
    },
    close() {
      document.removeEventListener("click", click, true);
      client?.close();
    },
  };
}
